import { Link, useSearchParams } from "react-router-dom";
import { useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Inbox,
  Loader2,
  MapPin,
  PackageCheck,
  RefreshCw,
  Store,
} from "lucide-react";

import DeliveryEssentials from "../components/DeliveryEssentials";
import RiderCommittedWorkCard from "../components/RiderCommittedWorkCard";
import { createClientRequestId } from "../../utils/clientRequestId";
import { useRiderPortal } from "../context/RiderPortalContext";
import { riderApi } from "../services/riderApi";

// WP9 slice 5 retired the offer statuses. Work is COMMITTED to a Rider, carried
// (PICKED_UP), completed, or cancelled - nothing is pending a response.
const STATUS_STYLES = {
  COMMITTED: "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200",
  PICKED_UP: "bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300",
  COMPLETED: "bg-stone-100 text-stone-700 dark:bg-white/10 dark:text-stone-300",
  CANCELLED: "bg-stone-100 text-stone-700 dark:bg-white/10 dark:text-stone-300",
};

// Live work is what the store has committed to this Rider and what is already in
// their hands. There is no offer queue to sort to the top any more.
const LIVE_STATUSES = ["COMMITTED", "PICKED_UP"];

function statusLabel(status) {
  return String(status || "UNKNOWN").replaceAll("_", " ").toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());
}

function assignmentError(error) {
  if (error?.code === "ASSIGNMENT_CONFLICT") return "This delivery changed elsewhere. We refreshed the latest status.";
  if (error?.code === "RIDER_NOT_ASSIGNMENT_ELIGIBLE") return "You're not currently eligible for this delivery. Check your duty status or contact your store.";
  return "We couldn't update this delivery. Check your connection and try again.";
}

function AssignmentCard({ assignment, busy, onReportProblem }) {
  const totalCommitted = Number(assignment.committedLoad || 0) + Number(assignment.activeLoad || 0);

  return (
    <article className="rounded-3xl border border-stone-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-neutral-900">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-stone-200">
            <Store className="h-4 w-4 shrink-0 text-emerald-600" />
            <span className="truncate">{assignment.storeName || "Store delivery"}</span>
          </div>
          <h2 className="mt-2 text-lg font-semibold">{assignment.orderReference || `Order #${assignment.orderId}`}</h2>
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLES[assignment.status] || STATUS_STYLES.CANCELLED}`}>
          {statusLabel(assignment.status)}
        </span>
      </div>

      <div className="mt-4 grid gap-3 rounded-2xl bg-stone-50 p-4 text-sm dark:bg-white/5">
        <div className="flex gap-3">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" />
          <div>
            <p className="font-medium">Destination area</p>
            <p className="mt-0.5 text-stone-500 dark:text-stone-400">{assignment.destinationArea || "Area details available from the store"}</p>
          </div>
        </div>
        {assignment.capacityLimit != null && (
          <div className="flex gap-3">
            <PackageCheck className="mt-0.5 h-4 w-4 shrink-0 text-stone-500" />
            <div>
              <p className="font-medium">Your load</p>
              <p className="mt-0.5 text-stone-500 dark:text-stone-400">{Math.min(totalCommitted, assignment.capacityLimit)} of {assignment.capacityLimit} planned capacity</p>
            </div>
          </div>
        )}
      </div>

      <DeliveryEssentials delivery={assignment.delivery} />
      <RiderCommittedWorkCard
        assignment={assignment}
        busy={busy}
        problem={assignment.openProblem || null}
        onReportProblem={onReportProblem}
      />
    </article>
  );
}

export default function RiderAssignments() {
  const { businessId, refreshContext, assignments = [], refreshing, stale, context } = useRiderPortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "work";
  const loading = !context;

  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const actionKeys = useRef(new Map());

  const runAction = async (assignment, action, payload = null) => {
    const reasonCode = typeof payload === 'string' ? payload : payload?.reasonCode || null;
    if (stale || busyId) return;
    const actionIdentity = `${assignment.id}:${assignment.version}:${action}`;
    const idempotencyKey = actionKeys.current.get(actionIdentity)
      || createClientRequestId(`assignment_${action}`);
    actionKeys.current.set(actionIdentity, idempotencyKey);
    setBusyId(assignment.id);
    setError("");
    try {
      // WP9 slice 5 retired accept and decline with the v1 offer. Reporting a
      // problem is what a Rider does with work they cannot complete: it records
      // facts and never releases the commitment.
      await riderApi.reportAssignmentProblem(businessId, assignment.id, {
        expectedAssignmentVersion: assignment.version,
        reasonCode,
        note: payload?.note,
        idempotencyKey,
      });
      actionKeys.current.delete(actionIdentity);
      await refreshContext();
    } catch (requestError) {
      setError(assignmentError(requestError));
      if (requestError?.code === "ASSIGNMENT_CONFLICT") {
        await refreshContext();
      }
    } finally {
      setBusyId(null);
    }
  };

  const ordered = useMemo(() => assignments.filter((assignment) => {
    if (view === "work") return LIVE_STATUSES.includes(assignment.status);
    if (view === "history") return !LIVE_STATUSES.includes(assignment.status);
    if (view === "committed") return assignment.status === "COMMITTED";
    if (view === "custody") return assignment.status === "PICKED_UP";
    return true;
  }).sort((left, right) => right.id - left.id), [assignments, view]);

  return (
    <div className="space-y-5">
      <section className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">Your deliveries</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">Assigned delivery work</h1>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">Work your store has committed to you. Open My deliveries for pickup and delivery.</p>
        </div>
        <button type="button" onClick={refreshContext} disabled={refreshing || loading} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-stone-200 bg-white disabled:opacity-50 dark:border-white/10 dark:bg-neutral-900" aria-label="Refresh assignments">
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium" htmlFor="assignment-view">Show assignments</label>
        <select id="assignment-view" value={view} onChange={(event) => setSearchParams(event.target.value === "work" ? {} : { view: event.target.value })} className="min-h-11 rounded-xl border border-stone-300 bg-white px-3 text-sm dark:border-white/15 dark:bg-neutral-900">
          <option value="work">Current work</option><option value="history">Delivery history</option><option value="committed">Committed - awaiting pickup</option><option value="custody">Parcels in custody</option>
        </select>
      </div>
      {Number(context?.duty?.committedLoad) > 0 && <p className="rounded-2xl bg-emerald-50 p-4 text-sm dark:bg-emerald-500/10">{context.duty.activeRun ? <Link to="/rider/run" className="font-semibold underline">Open your run for pickup and delivery steps</Link> : "Your parcels are reserved. Your pickup route is being prepared automatically. Open My deliveries to follow progress."}</p>}

      {error && (
        <div role="alert" className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div className="flex-1"><p>{error}</p><button type="button" onClick={refreshContext} className="mt-2 font-semibold underline">Try again</button></div>
        </div>
      )}

      {loading ? (
        <div className="grid min-h-52 place-items-center rounded-3xl border border-stone-200 bg-white dark:border-white/10 dark:bg-neutral-900">
          <div className="text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-emerald-600" /><p className="mt-3 text-sm text-stone-500">Loading assignments…</p></div>
        </div>
      ) : ordered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 p-8 text-center dark:border-white/15">
          <Inbox className="mx-auto h-7 w-7 text-stone-400" />
          <h2 className="mt-3 font-semibold">{view === "work" ? "No deliveries yet" : "No deliveries in this view"}</h2>
          <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">Deliveries this store commits to you will appear here.</p>
        </div>
      ) : (
        <section className="space-y-4" aria-label="Delivery assignments">
          {ordered.map((assignment) => (
            <AssignmentCard
              key={assignment.id}
              assignment={assignment}
              busy={Boolean(busyId) || stale}
              onReportProblem={(input) => runAction(assignment, "report-problem", input)}
            />
          ))}
        </section>
      )}
    </div>
  );
}
